import { useEffect, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { isAuMobile } from '../lib/reliability';

/**
 * "Book their next cut" — opens over the booking panel straight after
 * payment (backend rebook.ts). Suggests the same barber and services on the
 * customer's usual gap (4 weeks for a first visit), lists real free times,
 * and records the outcome either way so Reports can show a rebook rate.
 * Closing it counts as a skip.
 */

interface RebookBooking {
  id: string;
  location_id: number;
  barber_id: number;
  customer_name?: string;
  customer_phone?: string;
  services?: { name: string; duration?: number; duration_minutes?: number }[];
}

const BRISBANE_TZ = 'Australia/Brisbane';
/** When the suggested day has no free times, look this many days ahead. */
const AUTO_ADVANCE_DAYS = 7;

const dayLabel = (date: string) =>
  new Intl.DateTimeFormat('en-AU', {
    timeZone: BRISBANE_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${date}T12:00:00+10:00`));

const timeLabel = (iso: string) =>
  new Intl.DateTimeFormat('en-AU', {
    timeZone: BRISBANE_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(iso))
    .replace(' ', '')
    .toLowerCase();

const addDays = (date: string, days: number) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const todayBrisbane = () => new Date().toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });

const gapLabel = (days: number) =>
  days % 7 === 0 ? `${days / 7} weeks` : `${days} days (about ${Math.round(days / 7)} weeks)`;

const label: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--color-text-muted)',
  marginBottom: '6px',
};

const arrowButton: CSSProperties = {
  minWidth: '44px',
  minHeight: '44px',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
  backgroundColor: '#fff',
  fontSize: '18px',
  cursor: 'pointer',
};

export function RebookStep({
  booking,
  barbers,
  onDone,
}: {
  booking: RebookBooking;
  barbers: { id: number; name: string }[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const services = booking.services || [];
  const duration = services.reduce(
    (sum, s) => sum + (Number(s.duration) || Number(s.duration_minutes) || 0),
    0,
  );

  const { data: suggestion, error: suggestionError } = useQuery({
    queryKey: ['rebook-suggestion', booking.id],
    queryFn: () => api.getRebookSuggestion(String(booking.id)),
  });

  const [barberId, setBarberId] = useState<number | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<{ start: string; end: string } | null>(null);
  const [phone, setPhone] = useState('');
  const [autoAdvanceLeft, setAutoAdvanceLeft] = useState(AUTO_ADVANCE_DAYS);
  // True while the day shown is one we jumped to, not the barber's choice.
  const [autoMoved, setAutoMoved] = useState(false);
  const [created, setCreated] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // A booking made ahead needs a mobile (backend rebookAtTill) — ask for one
  // when the visit doesn't have it.
  const needsMobile = !isAuMobile(booking.customer_phone);
  const canBook = Boolean(slot) && (!needsMobile || isAuMobile(phone));

  // Start from the suggestion: same barber if they still work here.
  useEffect(() => {
    if (!suggestion || date) return;
    const same = barbers.some((b) => b.id === suggestion.barberId);
    setBarberId(same ? suggestion.barberId : (barbers[0]?.id ?? null));
    setDate(suggestion.suggestedDate);
  }, [suggestion, barbers, date]);

  const {
    data: availability,
    isFetching: slotsLoading,
    refetch: refetchSlots,
  } = useQuery({
    queryKey: ['rebook-availability', booking.location_id, barberId, date, duration],
    queryFn: () =>
      api.getAvailability(booking.location_id, barberId as number, date as string, duration),
    enabled: Boolean(barberId && date),
  });
  const slots = availability?.slots ?? [];

  // The suggested day may be a Sunday or the barber's day off — step forward
  // to the first day with free times (only until the barber picks a date).
  useEffect(() => {
    if (!availability || slotsLoading || !date) return;
    if (availability.slots.length === 0 && autoAdvanceLeft > 0) {
      setAutoAdvanceLeft((n) => n - 1);
      setAutoMoved(true);
      setDate(addDays(date, 1));
    }
  }, [availability, slotsLoading, date, autoAdvanceLeft]);

  const pickDate = (next: string) => {
    setAutoAdvanceLeft(0);
    setAutoMoved(false);
    setSlot(null);
    setErrorMsg(null);
    setDate(next);
  };
  const pickBarber = (id: number) => {
    setAutoAdvanceLeft(0);
    setAutoMoved(false);
    setSlot(null);
    setErrorMsg(null);
    setBarberId(id);
  };

  const bookMutation = useMutation({
    mutationFn: () =>
      api.rebook(String(booking.id), {
        startTime: (slot as { start: string }).start,
        barberId: barberId as number,
        customerPhone: needsMobile ? phone.trim() || undefined : undefined,
      }),
    onSuccess: (newBooking) => {
      setCreated(newBooking);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (err: any) => {
      setErrorMsg(err?.message || 'Could not book that time');
      setSlot(null);
      refetchSlots();
    },
  });

  const skip = () => {
    // Fire and forget — the barber shouldn't wait on the network to move on.
    api.rebookSkip(String(booking.id)).catch(() => {});
    onDone();
  };

  const barberName = barbers.find((b) => b.id === barberId)?.name ?? 'barber';
  const suggestedDay = suggestion?.suggestedDate;
  const isPastLimit = date ? date <= todayBrisbane() : true;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rebook-title"
      // Rendered inside the booking panel, whose backdrop closes on click —
      // keep clicks here from reaching it. Only Skip / Book / × close this.
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(17,17,17,0.42)',
        zIndex: 2100,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          maxHeight: '92vh',
          overflowY: 'auto',
          borderRadius: '16px',
          backgroundColor: 'var(--color-surface, #fff)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h2 id="rebook-title" style={{ margin: 0, fontSize: '18px' }}>
              {created ? 'Next cut booked' : 'Book their next cut?'}
            </h2>
            <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
              {booking.customer_name || 'Guest'} · {services.map((s) => s.name).join(' + ')} ·{' '}
              {duration} min
            </div>
          </div>
          <button
            type="button"
            aria-label={created ? 'Close' : 'Skip rebooking'}
            onClick={created ? onDone : skip}
            style={{ ...arrowButton, border: 'none', fontSize: '20px' }}
          >
            ×
          </button>
        </div>

        {created ? (
          <div style={{ marginTop: '16px' }}>
            <div
              style={{
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: 'rgba(40,167,69,0.08)',
                color: '#1e7e34',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              ✓{' '}
              {dayLabel(
                new Date(created.start_time).toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ }),
              )}{' '}
              at {timeLabel(created.start_time)} with {barberName}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '8px' }}>
              {created.customer_phone
                ? 'A confirmation text is on its way to the customer.'
                : 'No mobile on file, so no confirmation text was sent.'}
            </div>
            <button
              type="button"
              onClick={onDone}
              style={{
                width: '100%',
                minHeight: '44px',
                marginTop: '16px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                color: '#D9B256',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        ) : suggestionError ? (
          <div
            style={{ marginTop: '16px', fontSize: '13px', color: 'var(--color-error, #c0392b)' }}
          >
            {(suggestionError as Error).message}
          </div>
        ) : (
          <>
            {suggestion && (
              <div style={{ fontSize: '13px', marginTop: '12px', color: 'var(--color-text)' }}>
                {suggestion.basis === 'usual'
                  ? `Usually comes every ${gapLabel(suggestion.gapDays)}.`
                  : `First visit — suggesting ${gapLabel(suggestion.gapDays)}.`}
                {autoMoved && date && suggestedDay && date !== suggestedDay && (
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {' '}
                    No free times on {dayLabel(suggestedDay)}, showing the next free day.
                  </span>
                )}
              </div>
            )}

            <div style={{ marginTop: '14px' }}>
              <div style={label}>Barber</div>
              <select
                value={barberId ?? ''}
                onChange={(e) => pickBarber(Number(e.target.value))}
                style={{
                  width: '100%',
                  minHeight: '44px',
                  padding: '6px 10px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: '#fff',
                }}
              >
                {barbers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: '14px' }}>
              <div style={label}>Day</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  aria-label="Previous day"
                  disabled={isPastLimit}
                  onClick={() => date && pickDate(addDays(date, -1))}
                  style={{ ...arrowButton, opacity: isPastLimit ? 0.4 : 1 }}
                >
                  ‹
                </button>
                <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: '15px' }}>
                  {date ? dayLabel(date) : '…'}
                  {date && date === suggestedDay && (
                    <span
                      style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        backgroundColor: 'rgba(217,178,86,0.15)',
                        color: '#9a7a2c',
                      }}
                    >
                      Suggested
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Next day"
                  onClick={() => date && pickDate(addDays(date, 1))}
                  style={arrowButton}
                >
                  ›
                </button>
              </div>
            </div>

            <div style={{ marginTop: '14px' }}>
              <div style={label}>Time</div>
              {slotsLoading || !date ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  Finding free times…
                </div>
              ) : slots.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  No free times for {barberName} on this day — try another day or barber.
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
                    gap: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {slots.map((s) => {
                    const active = slot?.start === s.start;
                    return (
                      <button
                        key={s.start}
                        type="button"
                        aria-pressed={active}
                        onClick={() => {
                          setSlot(s);
                          setErrorMsg(null);
                        }}
                        style={{
                          minHeight: '44px',
                          borderRadius: '8px',
                          border: active
                            ? '1px solid rgba(217,178,86,0.8)'
                            : '1px solid var(--color-border)',
                          backgroundColor: active ? 'rgba(217,178,86,0.18)' : '#fff',
                          fontWeight: active ? 700 : 500,
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        {timeLabel(s.start)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {needsMobile && (
              <label style={{ display: 'block', marginTop: '14px' }}>
                <div style={label}>Mobile for the reminder text *</div>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="04xx xxx xxx"
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    padding: '6px 10px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    boxSizing: 'border-box',
                  }}
                />
              </label>
            )}

            {errorMsg && (
              <div
                role="alert"
                style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: 'var(--color-error, #c0392b)',
                }}
              >
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '18px' }}>
              <button
                type="button"
                onClick={skip}
                style={{
                  flex: 1,
                  minHeight: '44px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: '#fff',
                  color: 'var(--color-text-muted)',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                disabled={!canBook || bookMutation.isPending}
                onClick={() => bookMutation.mutate()}
                style={{
                  flex: 2,
                  minHeight: '44px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                  color: '#D9B256',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: canBook ? 'pointer' : 'not-allowed',
                  opacity: canBook ? 1 : 0.5,
                }}
              >
                {bookMutation.isPending
                  ? 'Booking…'
                  : slot && date
                    ? needsMobile && !isAuMobile(phone)
                      ? 'Add their mobile to book'
                      : `Book ${dayLabel(date)}, ${timeLabel(slot.start)}`
                    : 'Pick a time'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
